import { AmendmentModel, type FilingObligationDto } from '@signalvote/shared';
import {
  filingDestinationPhrase,
  type PortalUploadModel,
} from '@/hooks/finance/usePortalUploadModel';
import { receivingOfficePhrase } from '../reports/receivingOffice';
import { formatAmount, formatDate } from '../utils';
import { noticeLabel } from './noticeCountdown';

/**
 * Copy for the action-queue confirm dialogs — the ADR-023 treatment for the
 * four queue actions (Generate / Mark-as-reported / daily Mark-filed / the
 * "filed elsewhere" dismiss ×). One pure builder per action so the dialogs stay
 * declarative and the copy is testable without rendering.
 *
 * The transaction under confirmation is a structured LINE ITEM (primary /
 * amount / meta), not prose: the dialog's job is verification before a legal
 * assertion — "is this the gift I actually filed?" — and a ledger-like row
 * keeps the amount scannable, which also survives the large-font accessibility
 * scaling our users run. Only the consequence stays a sentence.
 *
 * Jurisdiction seam: every destination name derives from
 * `filingDestinationPhrase` over the portal-upload model (the one place the
 * filing-model boundary is resolved) — never a hardcoded "Alabama" /
 * "FCPA portal". Rapid items degrade to the obligation name when structured
 * donor details are absent (deadline-mapped rapids have `donorName` null).
 *
 * Committing actions (mark-reported, daily mark-filed, dismiss) carry the same
 * append-only trust contract as `MarkFiledInPortalButton`: the flip means
 * "won't be re-exported", so attesting without having filed silently
 * under-reports. Generate is the odd one out — it's free and repeatable
 * (ADR-019, generate ≠ file), so its dialog fights ignorance, not danger: it
 * restates that generating files nothing and the portal upload is on you.
 */

type ConfirmModel = Pick<
  PortalUploadModel,
  'isPortalUpload' | 'stateDisplayName' | 'portalName' | 'amendmentModel'
> & {
  // #860 — carry the served transport axis so `filingDestinationPhrase` names an email receiving
  // office (NC → NCSBE, MS → the Secretary of State / Circuit Clerk), not a "portal" word. Optional
  // so a portal-jurisdiction caller (and the copy tests) needn't supply it — the real callers spread
  // the full `usePortalUploadModel()`, which carries it.
  filingTransport?: PortalUploadModel['filingTransport'];
};

// An email-transport jurisdiction (NC, MS) has a receiving office, not a portal: the treasurer emails
// the filing to it, so its copy never says "portal" or "upload".
function isEmailTransport(model: ConfirmModel): boolean {
  return model.filingTransport === 'email';
}

// A 48-hour notice the app can't build is filed in the state's portal even where the periodic report is
// emailed (North Carolina files it in the NCSBE CFRS portal), so only a notice the app builds is emailed.
export function noticeFilingModel<M extends Pick<ConfirmModel, 'filingTransport'>>(
  o: Pick<FilingObligationDto, 'canGenerate'>,
  model: M,
): M {
  return o.canGenerate ? model : { ...model, filingTransport: null };
}

function filedWhere(model: ConfirmModel): string {
  return isEmailTransport(model)
    ? `with ${receivingOfficePhrase(model.portalName)}`
    : `in ${filingDestinationPhrase(model)}`;
}

function filedConfirmLabel(model: ConfirmModel): string {
  return isEmailTransport(model)
    ? `Yes, I emailed it to ${receivingOfficePhrase(model.portalName)}`
    : 'Yes, I filed it in the portal';
}

// The portal artifact keeps its form name (the federal notice uploads as a Form 6); the emailed one is
// named for what it is.
function generateDeliveryStep(
  model: ConfirmModel,
  artifact: { emailed: string; uploaded: string },
): string {
  return isEmailTransport(model)
    ? `you'll email the ${artifact.emailed} to ${receivingOfficePhrase(model.portalName)}`
    : `you'll upload the ${artifact.uploaded} in ${filingDestinationPhrase(model)}`;
}

/** The ledger-like row the dialog renders above the consequence sentence. */
export interface QueueConfirmItem {
  primary: string;
  amount?: string;
  meta?: string;
}

export interface QueueConfirmCopy {
  title: string;
  item: QueueConfirmItem;
  /** The consequence sentence below the item — the ADR-023 trust contract for
   *  committing actions; the ADR-019 generate ≠ file restatement for Generate. */
  consequence: string;
  confirmLabel: string;
}

/** "Acme PAC / $25,000.00 / Contribution · received Jun 8, 2026" — or the bare
 *  obligation name when structured donor details are absent. */
function rapidItem(o: FilingObligationDto): QueueConfirmItem {
  if (o.donorName && o.contributionAmount != null) {
    return {
      primary: o.donorName,
      amount: formatAmount(o.contributionAmount),
      meta: o.contributionDate
        ? `Contribution · received ${formatDate(o.contributionDate)}`
        : 'Contribution',
    };
  }
  return { primary: o.name };
}

export function generateRapidConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  return {
    title: 'Generate the rapid report?',
    item: rapidItem(o),
    consequence:
      `Generating doesn't file anything — ${generateDeliveryStep(model, { emailed: 'file', uploaded: 'file' })}, `
      + 'then come back and mark it reported.',
    confirmLabel: 'Generate & download',
  };
}

export function markRapidReportedConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  // On a WHOLE-REPORT jurisdiction (anything but AL's append-only PortalOnly — VA/LA
  // SelfContainedUpload, federal ElectronicResubmit) the rapid report is SUPPLEMENTAL: the gift
  // ALSO belongs on the regular periodic report, and marking the rapid reported does NOT flip the
  // contribution or remove it from any export. AL is append-only PortalOnly, so there marking
  // reported IS what keeps the gift out of the next upload ("won't be re-exported"). #571 — read off
  // the served `amendmentModel`; default (null / unresolved) to the conservative append-only wording.
  const isSupplemental =
    model.amendmentModel != null && model.amendmentModel !== AmendmentModel.PortalOnly;
  return {
    title: 'Record this rapid report as filed?',
    item: rapidItem(o),
    consequence: isSupplemental
      ? `This records that you filed the rapid report ${filedWhere(model)}. The contribution still `
        + "appears on your regular periodic report — marking it here doesn't remove it. Only do "
        + 'this if you actually filed the rapid report.'
      : "This records it as reported — it won't be included in any export. Only do "
        + `this if you actually filed the rapid report ${filedWhere(model)}.`,
    confirmLabel: filedConfirmLabel(model),
  };
}

/**
 * The VA §24.2-947.11 pre-session report's mark-reported confirm (GitHub #164,
 * ADR-039). The report is WHOLE-WINDOW and multi-donor (`ContributionId`-null),
 * so the line item is the obligation name — there is no single gift to show. It
 * is SUPPLEMENTAL: marking it reported flips only the obligation, not the gifts,
 * which still file on the next periodic report by design — so the copy states
 * that plainly and never the append-only "won't be re-exported" (which would be
 * flatly wrong here). Destination stays jurisdiction-seamed via the model even
 * though this obligation only arises for VA (COMET).
 */
export function markPreSessionReportedConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  return {
    title: 'Record this pre-session report as filed?',
    item: { primary: o.name },
    consequence:
      `This records that you filed the pre-session report ${filedWhere(model)}. Those `
      + "contributions still appear on your next periodic report — marking it here doesn't remove "
      + 'them. Only do this if you actually filed the pre-session report.',
    confirmLabel: filedConfirmLabel(model),
  };
}

/**
 * The federal 11 CFR 104.5(f) 48-hour notice's GENERATE confirm (GitHub #708). The federal Form 6
 * emitter exists, so — unlike NC — the notice has a generate step, and it needs the same ADR-019
 * restatement `generateRapidConfirmCopy` carries: generating builds the file, it does not file it.
 * Named for the NOTICE rather than reusing the rapid copy because the artifact is a Form 6 and the
 * treasurer should see that word before uploading it.
 */
export function generateNoticeConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  return {
    title: `Generate the ${noticeLabel(o)}?`,
    item: rapidItem(o),
    consequence:
      `Generating doesn't file anything — ${generateDeliveryStep(model, { emailed: '48-hour notice', uploaded: 'Form 6' })}, `
      + 'then come back and mark it reported.',
    confirmLabel: 'Generate & download',
  };
}

/**
 * A 48-hour notice's mark-reported confirm — NC §163-278.9(a)(2) (GitHub #301, ADR-038) and the
 * federal 11 CFR 104.5(f) Form 6 notice (#708) share it. A SINGLE-gift
 * notice, so the line item is the donor row (`rapidItem`, degrading to the obligation name when donor
 * details are absent). It is SUPPLEMENTAL like the VA pre-session report: the gift ALSO files on the
 * next periodic report, so marking the notice reported removes it from no export — the copy states that
 * plainly and never the append-only "won't be re-exported" (which would be flatly wrong here). Unlike
 * `markRapidReportedConfirmCopy`, it doesn't branch on the model — a 48-hour notice is
 * unconditionally supplemental. Destination stays jurisdiction-seamed via the model.
 */
export function markNoticeReportedConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  const filing = noticeFilingModel(o, model);
  const label = noticeLabel(o);
  return {
    title: `Record this ${label} as filed?`,
    item: rapidItem(o),
    consequence:
      `This records that you filed the ${label} ${filedWhere(filing)}. The `
      + "contribution still appears on your regular periodic report — marking it here doesn't "
      + `remove it. Only do this if you actually filed the ${label}.`,
    confirmLabel: filedConfirmLabel(filing),
  };
}

export function dailyMarkFiledConfirmCopy(
  o: FilingObligationDto,
  model: ConfirmModel,
): QueueConfirmCopy {
  return {
    title: 'Record this daily report as filed?',
    item: {
      primary: formatDate(o.obligationDate ?? o.dueDate),
      amount: formatAmount(o.dayGrossAmount ?? 0),
      meta: 'Combined receipts and spending that day',
    },
    consequence:
      `This records the day's report as filed ${filedWhere(model)} — only do this `
      + 'if you actually filed it there.',
    confirmLabel: filedConfirmLabel(model),
  };
}

export function dismissFiledElsewhereConfirmCopy(o: FilingObligationDto): QueueConfirmCopy {
  // The attestation commits exactly these period bounds — name them. (Dismiss
  // is only offered when the obligation carries a period, so the meta is
  // effectively always present; the guard just keeps the builder total.)
  const period =
    o.periodStart && o.periodEnd
      ? `Period ${formatDate(o.periodStart)} – ${formatDate(o.periodEnd)}`
      : undefined;
  return {
    title: 'Already filed elsewhere?',
    item: period ? { primary: o.name, meta: period } : { primary: o.name },
    consequence:
      "This records it was already filed outside this app — it won't be tracked "
      + "here anymore. Only dismiss it if it's really filed.",
    confirmLabel: "Yes, it's already filed",
  };
}
